import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Zap, Target, Globe2, Shield, Star, ChevronDown,
  ArrowRight, CheckCircle, Quote
} from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';

const WHATSAPP_URL = 'https://wa.me/919663112394?text=Hi!%20I%27m%20interested%20in%20a%20free%20consultation';

const Trust = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const valueProps = [
    {
      icon: Zap,
      title: 'Speed',
      desc: "We don't waste time. You're live in 48 hours, not 48 days.",
      color: 'bg-primary-500'
    },
    {
      icon: Target,
      title: 'Specialized',
      desc: 'Built specifically for service businesses. Not generic templates.',
      color: 'bg-secondary-500'
    },
    {
      icon: Globe2,
      title: 'Made for India',
      desc: 'WhatsApp-first. Works on Jio. Speaks your language.',
      color: 'bg-accent-500'
    },
    {
      icon: Shield,
      title: 'No Lock-In',
      desc: 'Cancel anytime. Own your domain. No hidden fees.',
      color: 'bg-primary-600'
    }
  ];

  const testimonials = [
    {
      quote: "MakeMyDukaan built our salon website in 2 days. We now get 15+ bookings daily from Google!",
      name: 'Priya Sharma',
      business: 'Glamour Studio, Mumbai',
      type: 'Salon',
      rating: 5
    },
    {
      quote: "The best investment for our clinic. Patients find us online and book appointments without calling. Incredible!",
      name: 'Dr. Rajesh Kumar',
      business: 'HealthFirst Clinic, Delhi',
      type: 'Clinic',
      rating: 5
    },
    {
      quote: "Our spa's online presence has transformed completely. The booking system alone has saved us hours every week.",
      name: 'Kavya Nair',
      business: 'Serenity Spa, Bangalore',
      type: 'Spa',
      rating: 5
    },
    {
      quote: "From zero online presence to 200+ monthly leads. MakeMyDukaan understood exactly what we needed.",
      name: 'Amit Patel',
      business: 'FitZone Studio, Pune',
      type: 'Fitness',
      rating: 5
    }
  ];

  const faqs = [
    {
      q: 'Do I need technical knowledge?',
      a: "Absolutely not! We handle everything from design to deployment. You just tell us about your business, and we take care of the rest. You'll get a simple admin panel to manage your content."
    },
    {
      q: 'Can I use my existing domain?',
      a: "Yes! If you already have a domain, we'll connect it to your new website. If you don't have one, we'll help you get a professional domain as part of your plan."
    },
    {
      q: 'What if I need changes later?',
      a: "All our plans include unlimited revisions during the build phase. After launch, you can request changes through our support channels. Minor updates are included in your monthly plan."
    },
    {
      q: 'Do you provide content/photos?',
      a: "Our Professional plan includes a free photoshoot. For all plans, we can use high-quality stock photos and help you write compelling content for your website."
    },
    {
      q: 'Is WhatsApp booking included?',
      a: "Yes! WhatsApp integration is a core feature in all our plans. Customers can book appointments and reach you directly through WhatsApp from your website."
    },
    {
      q: 'What about SEO and Google ranking?',
      a: "Every website we build is SEO-optimized from day one. We set up Google Business Profile, add local SEO, proper meta tags, and fast-loading pages to help you rank higher."
    }
  ];

  return (
    <>
      {/* Why Choose Us */}
      <section className="py-20 lg:py-28 bg-white" data-testid="why-choose-section">
        <div className="container">
          <motion.div
            className="text-center mb-14"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-block px-4 py-2 bg-secondary-50 text-secondary-600 font-medium rounded-full text-sm mb-4">
              Why Us
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-neutral-900">
              Why MakeMyDukaan?
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {valueProps.map((prop, i) => (
              <motion.div
                key={prop.title}
                className="card-base text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                data-testid={`value-prop-${i}`}
              >
                <div className={`w-14 h-14 ${prop.color} rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-lg`}>
                  <prop.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-heading font-bold text-lg text-neutral-900 mb-2">{prop.title}</h3>
                <p className="text-neutral-500 text-sm leading-relaxed">{prop.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 lg:py-28 bg-section-alt" data-testid="testimonials-section">
        <div className="container">
          <motion.div
            className="text-center mb-14"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-block px-4 py-2 bg-accent-50 text-accent-600 font-medium rounded-full text-sm mb-4">
              Testimonials
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-neutral-900">
              What Our Clients Say
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {testimonials.map((t, i) => (
              <motion.div
                key={t.name}
                className="testimonial-card bg-white rounded-2xl p-7 border border-neutral-100"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                data-testid={`testimonial-${i}`}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(t.rating)].map((_, j) => (
                    <Star key={j} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <Quote className="w-8 h-8 text-primary-200 mb-3" />
                <p className="text-neutral-700 leading-relaxed mb-5">"{t.quote}"</p>
                <div className="flex items-center gap-3 pt-4 border-t border-neutral-100">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-400 to-secondary-400 flex items-center justify-center text-white font-bold text-sm">
                    {t.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div>
                    <p className="font-heading font-bold text-neutral-900 text-sm">{t.name}</p>
                    <p className="text-neutral-500 text-xs">{t.business}</p>
                  </div>
                  <span className="ml-auto px-2.5 py-1 bg-primary-50 text-primary-600 text-xs font-medium rounded-lg">
                    {t.type}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 lg:py-28 bg-white" data-testid="faq-section">
        <div className="container">
          <motion.div
            className="text-center mb-14"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-block px-4 py-2 bg-primary-50 text-primary-600 font-medium rounded-full text-sm mb-4">
              FAQ
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-neutral-900">
              Frequently Asked Questions
            </h2>
          </motion.div>

          <div className="max-w-3xl mx-auto space-y-3">
            {faqs.map((faq, i) => (
              <motion.div
                key={i}
                className="border border-neutral-200 rounded-xl overflow-hidden"
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                data-testid={`faq-${i}`}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-neutral-50 transition-colors"
                  data-testid={`faq-toggle-${i}`}
                >
                  <span className="font-heading font-bold text-neutral-900 pr-4">{faq.q}</span>
                  <ChevronDown className={`w-5 h-5 text-neutral-400 flex-shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {openFaq === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <div className="px-5 pb-5 text-neutral-600 text-sm leading-relaxed">
                        {faq.a}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 lg:py-28 bg-gradient-to-br from-primary-600 via-primary-500 to-secondary-500 relative overflow-hidden" data-testid="final-cta-section">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-20 w-96 h-96 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-10 left-20 w-72 h-72 bg-accent-300 rounded-full blur-3xl" />
        </div>

        <div className="container relative z-10">
          <motion.div
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-5">
              Ready to Go Digital?
            </h2>
            <p className="text-white/80 text-lg mb-10 max-w-xl mx-auto">
              Join 100+ businesses already growing with MakeMyDukaan. Your premium website is just 48 hours away.
            </p>

            <div className="flex flex-wrap justify-center gap-4 mb-10">
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white text-primary-600 px-8 py-4 rounded-xl font-bold text-base hover:bg-neutral-50 transition-all hover:-translate-y-1 shadow-xl inline-flex items-center gap-2"
                data-testid="final-cta-consultation"
              >
                <FaWhatsapp className="w-5 h-5" />
                Start Free Consultation
              </a>
              <a
                href="#examples"
                className="border-2 border-white/40 text-white px-8 py-4 rounded-xl font-bold text-base hover:bg-white/10 transition-all hover:-translate-y-1 inline-flex items-center gap-2"
                data-testid="final-cta-examples"
              >
                See Live Examples
                <ArrowRight className="w-5 h-5" />
              </a>
            </div>

            <div className="flex flex-wrap justify-center gap-6 text-white/80">
              {[
                '48-hour delivery guarantee',
                '100% satisfaction or refund',
                'Trusted by 100+ businesses'
              ].map((badge) => (
                <div key={badge} className="flex items-center gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-accent-300" />
                  {badge}
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Trust;
